/**
 * "Send it again" after a bounce (lifecycle audit, 10/29) — the pure part.
 * Turns one bounced item from GET /customers/:id/bounced-sends into the
 * existing send call(s) that re-send it to the fixed address. No new send
 * path: invoices and quotes use their own Send, a packet uses Approve & Send
 * (a resend, so nothing is re-approved).
 */
export type BouncedSendKind = "packet" | "invoice" | "quote" | "other";

export interface BouncedSendItem {
    sendId: string;
    at: string;
    recipient: string;
    reason: string | null;
    kind: BouncedSendKind;
    documents: string[];
    jobId: string | null;
    jobNumber: string | null;
    invoices: Array<{ id: string; number: string | null }>;
    quotes: Array<{ id: string; number: string | null }>;
    sendTo: string | null;
    sendToContactMethodId: string | null;
}

export type SendAgainCall = { method: "post" | "patch"; url: string; body: Record<string, unknown> };

const PACKET_DOCS = new Set(["report", "invoice", "estimate", "photos"]);

/** The calls that re-send this item, or [] when it can't be re-sent in one tap. */
export function sendAgainCalls(item: BouncedSendItem): SendAgainCall[] {
    const to = item.sendTo;
    if (!to) return [];
    if (item.kind === "invoice") {
        return item.invoices.map((i) => ({
            method: "post" as const,
            url: `/api/invoices/${i.id}/send`,
            body: { recipients: [to] },
        }));
    }
    if (item.kind === "quote") {
        return item.quotes.map((q) => ({
            method: "patch" as const,
            url: `/api/estimates/${q.id}/send`,
            body: { recipients: [to] },
        }));
    }
    if (item.kind === "packet" && item.jobId) {
        const docs = item.documents.filter((d) => PACKET_DOCS.has(d));
        if (docs.length === 0) return [];
        return [
            {
                method: "post",
                url: `/api/office-review/jobs/${item.jobId}/approve-and-send`,
                body: {
                    invoices: docs.includes("invoice"),
                    estimates: docs.includes("estimate"),
                    reports: docs.includes("report"),
                    photos: docs.includes("photos"),
                    plan: [
                        {
                            value: to,
                            source: "saved",
                            contactMethodId: item.sendToContactMethodId,
                            docs,
                        },
                    ],
                },
            },
        ];
    }
    return [];
}

/** What the item was, in the office's words. */
export function bouncedItemLabel(item: BouncedSendItem): string {
    const nums = (xs: Array<{ number: string | null }>) =>
        xs
            .map((x) => x.number)
            .filter(Boolean)
            .join(", ");
    if (item.kind === "invoice") return `Invoice ${nums(item.invoices) || ""}`.trim();
    if (item.kind === "quote") return `Quote ${nums(item.quotes) || ""}`.trim();
    if (item.kind === "packet") return `Job packet${item.jobNumber ? ` #${item.jobNumber}` : ""}`;
    return "Email";
}
